import React, {useEffect, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Container,
    List,
    ListItem,
    ListItemText,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import {Cancel, CheckCircle, HelpOutline} from "@mui/icons-material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {useTranslation} from "react-i18next";

import {getMyCodeTasksByLesson} from "../api/api";
import {CodeTaskListResponse} from "../types";
import {useAlert} from "../context/AlertContext";
import BreadcrumbLink from "../components/BreadcrumbLink";

const MyTasksPage = () => {
    const {t} = useTranslation();

    const {lessonId} = useParams<{ lessonId?: string }>();
    const safeLessonId = lessonId ?? "";

    const [data, setData] = useState<CodeTaskListResponse | null>(null);
    const {showAlert} = useAlert();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!safeLessonId) return;

        let cancelled = false;
        setLoading(true);

        getMyCodeTasksByLesson(Number(safeLessonId))
            .then((res) => {
                if (!cancelled) setData(res);
            })
            .catch((error: any) => {
                if (!cancelled) {
                    showAlert(error?.message ?? t("mycodetasks.errors.loadFailed"), "error");
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeLessonId]);

    if (loading && !data) {
        return (
            <Container sx={{mt: 6, display: "flex", justifyContent: "center"}}>
                <CircularProgress/>
            </Container>
        );
    }

    if (!data) {
        return (
            <Container sx={{mt: 4}}>
                <Button component={Link} to="/" variant="outlined">
                    {t("common.toMainPage")}
                </Button>
            </Container>
        );
    }

    return (
        <Box
            sx={{
                pb: 3,
                px: {xs: 3, md: 3},
            }}
        >
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small"/>}
                maxItems={3}
                itemsBeforeCollapse={1}
                itemsAfterCollapse={2}
                sx={{
                    mb: 2,
                    "& .MuiBreadcrumbs-ol": {
                        overflow: "hidden",
                    },
                    "& .MuiBreadcrumbs-li": {
                        minWidth: 0,
                    },
                }}
            >
                <BreadcrumbLink to="/my-courses">
                    {t("breadcrumb.myCourses")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/course-details/${data.courseId}`}>
                    {data.courseTitle}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/chapter-details/${data.chapterId}`}>
                    {data.chapterTitle}
                </BreadcrumbLink>

                <Typography color="text.primary" variant="body2">
                    <strong>{data.lessonTitle}</strong>
                </Typography>
            </Breadcrumbs>

            <Box sx={{mb: 3}}>
                <Tabs
                    value="code-tasks"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 48,
                        px: 0,
                        border: 0,
                        backgroundColor: "transparent",

                        "& .MuiTabs-scroller": {
                            border: 0,
                        },

                        "& .MuiTabs-flexContainer": {
                            gap: 1,
                        },

                        "& .MuiTabs-indicator": {
                            height: 3,
                            borderRadius: "3px 3px 0 0",
                        },

                        "& .MuiTab-root": {
                            minHeight: 48,
                            minWidth: "auto",
                            px: 2.5,
                            py: 1,
                            border: 0,
                            borderRadius: 0,
                            boxShadow: "none",
                            fontWeight: 700,
                            textTransform: "none",
                            color: "text.secondary",
                            backgroundColor: "transparent",

                            "&:hover": {
                                backgroundColor: "action.hover",
                            },

                            "&.Mui-selected": {
                                color: "primary.main",
                            },

                            "&.Mui-focusVisible": {
                                outline: "none",
                            },
                        },
                    }}
                >
                    <Tab
                        value="lesson"
                        label={t("tabs.lesson")}
                        component={Link}
                        to={`/lesson-details/${data.lessonId}`}
                    />

                    <Tab
                        value="code-tasks"
                        label={t("tabs.codeTasks")}
                        component={Link}
                        to={`/lesson-details/${data.lessonId}/tasks`}
                    />

                    <Tab
                        value="practice-tasks"
                        label={t("tabs.practiceTasks")}
                        component={Link}
                        to={`/lesson-details/${data.lessonId}/practices`}
                    />
                </Tabs>
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography sx={{fontSize: 28, fontWeight: 800}} noWrap>
                    {data.lessonTitle}
                </Typography>
            </Box>

            {data.tasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {t("mycodetasks.noCodeTasks")}
                </Typography>
            ) : (
                <Box
                    sx={(theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 2,
                    })}
                >
                    <List sx={{p: 0}}>
                        {data.tasks
                            .slice()
                            .sort((a, b) => a.orderNum - b.orderNum)
                            .map((task, index) => (
                                <ListItem
                                    key={task.id}
                                    component={Link}
                                    to={`/task-details/${task.id}`}
                                    sx={(theme) => ({
                                        borderBottom:
                                            index === data.tasks.length - 1
                                                ? "none"
                                                : `1px solid ${theme.palette.divider}`,
                                        textDecoration: "none",
                                        color: "inherit",
                                        "&:hover": {
                                            backgroundColor: theme.palette.action.hover,
                                        },
                                    })}
                                >
                                    <ListItemText
                                        primary={
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="subtitle1" sx={{color: "text.primary"}}>
                                                    {task.title}
                                                </Typography>

                                                {task.solved === true && (
                                                    <CheckCircle fontSize="small" color="success"/>
                                                )}

                                                {task.solved === false && (
                                                    <Cancel fontSize="small" color="error"/>
                                                )}

                                                {task.solved === null && (
                                                    <HelpOutline fontSize="small" color="disabled"/>
                                                )}
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            ))}
                    </List>
                </Box>
            )}
        </Box>
    );
};

export default MyTasksPage;